import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Send,
  Heart,
  Info,
  Brush,
  Layers,
  Plus,
  X,
} from 'lucide-react';
import { getImage, listImages, setFavorite, setRating } from '../api/images';
import { authorOf, listComments, postComment } from '../api/comments';
import { listEdits, editedImageUrl } from '../api/edits';
import type { ImageEdit } from '../api/edits';
import { useAuth } from '../hooks/useAuth';
import { useFilterStore } from '../stores/filterStore';
import { useKeyboard } from '../hooks/useKeyboard';
import { useWorkspaceId } from '../hooks/useWorkspace';
import { setLastFocusedImage } from '../hooks/useScrollRestoration';
import { StarRating } from '../components/ui/StarRating';
import { HeartFavorite } from '../components/ui/HeartFavorite';
import { DownloadButton } from '../components/ui/DownloadButton';
import { ZoomableImage, type ZoomableImageHandle } from '../components/detail/ZoomableImage';
import { HealingTool } from '../components/detail/HealingTool';
import { AddToSetPopover } from '../components/sets/AddToSetPopover';
import { getImageSets, getSetImages, getSet, removeImageFromSet, type ImageSetMembership } from '../api/sets';
import type { Comment, ImageDetail, UserName } from '../api/types';

const COMMENTS_REFETCH_MS = 5000;

export default function Detail() {
  // Två URL-pattern stödjs:
  //   /shoots/:shootId/images/:imageId  → prev/next inom shoot, back till shoot
  //   /sets/:setId/images/:imageId      → prev/next inom set, back till set
  const { shootId, setId, imageId } = useParams<{
    shootId?: string;
    setId?: string;
    imageId: string;
  }>();
  const inSetContext = !!setId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const filters = useFilterStore();
  const workspaceId = useWorkspaceId();

  const [panelHidden, setPanelHidden] = useState(false);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [draftMessage, setDraftMessage] = useState('');
  const [isZoomed, setIsZoomed] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showOriginalView, setShowOriginalView] = useState(false);
  const [addSetOpen, setAddSetOpen] = useState(false);
  const addSetBtnRef = useRef<HTMLButtonElement>(null);
  const zoomRef = useRef<ZoomableImageHandle>(null);

  const filtersForQuery = useMemo(
    () => ({ stars: filters.stars, user: filters.user, favorites: filters.favorites }),
    [filters.stars, filters.user, filters.favorites],
  );

  // I shoot-kontext: hämta bilder filtrerat. I set-kontext: hämta set:s bilder
  // (utan filter — filter används inte i sets än, kan läggas till senare).
  const shootImagesQuery = useQuery({
    queryKey: ['shoot', shootId, 'images', filtersForQuery],
    queryFn: () => listImages(shootId!, filtersForQuery),
    enabled: !!shootId && !inSetContext,
  });

  const setImagesQuery = useQuery({
    queryKey: ['set', setId, 'images'],
    queryFn: () => getSetImages(setId!),
    enabled: !!setId && inSetContext,
  });

  // För back-länken behöver vi set-namn (om vi är i set-kontext)
  const setQuery = useQuery({
    queryKey: ['set', setId],
    queryFn: () => getSet(setId!),
    enabled: !!setId && inSetContext,
  });

  const imagesQuery = inSetContext ? setImagesQuery : shootImagesQuery;

  const imageQuery = useQuery({
    queryKey: ['image', imageId],
    queryFn: () => getImage(imageId!),
    enabled: !!imageId,
  });

  const commentsQuery = useQuery({
    queryKey: ['image', imageId, 'comments'],
    queryFn: () => listComments(imageId!),
    enabled: !!imageId,
    refetchInterval: COMMENTS_REFETCH_MS,
  });

  const editsQuery = useQuery({
    queryKey: ['image', imageId, 'edits'],
    queryFn: () => listEdits(imageId!),
    enabled: !!imageId,
  });

  // Sets bilden tillhör (för chips i sidopanelen).
  const imageSetsQuery = useQuery({
    queryKey: ['image', imageId, 'sets'],
    queryFn: () => getImageSets(imageId!),
    enabled: !!imageId,
  });

  const image = imageQuery.data;
  const images = imagesQuery.data ?? [];
  const comments = commentsQuery.data ?? [];
  const edits: ImageEdit[] = editsQuery.data ?? [];
  const hasEdits = edits.length > 0;
  const imageSets = imageSetsQuery.data ?? [];

  const currentIdx = images.findIndex((img) => img.id === imageId);
  const prevImage = currentIdx > 0 ? images[currentIdx - 1] : undefined;
  const nextImage = currentIdx >= 0 && currentIdx < images.length - 1 ? images[currentIdx + 1] : undefined;

  const stagePreviewUrl = useMemo(() => {
    if (!image) return '';
    if (hasEdits && !showOriginalView) {
      return editedImageUrl(image.id, 'preview', 'latest');
    }
    return editedImageUrl(image.id, 'preview');
  }, [image, hasEdits, showOriginalView]);

  const stageFullUrl = useMemo(() => {
    if (!image) return '';
    if (hasEdits && !showOriginalView) {
      return editedImageUrl(image.id, 'full', 'latest');
    }
    return editedImageUrl(image.id, 'full');
  }, [image, hasEdits, showOriginalView]);

  const stageImageUrl = stagePreviewUrl;

  useEffect(() => {
    setEditMode(false);
    setShowOriginalView(false);
  }, [imageId]);

  useEffect(() => {
    if (shootId && imageId) setLastFocusedImage(shootId, imageId);
  }, [shootId, imageId]);

  // Helper: invalidera ALLA listor som innehåller den här bilden, oavsett
  // om vi öppnade Detail från shoot- eller set-context. Tidigare invaliderade
  // vi bara `['shoot', shootId, 'images']`, vilket missade `['set', setId,
  // 'images']` när användaren ratar bilder i set-context. Resultat: ratings
  // verkade "fastna" tills sidan laddades om.
  function invalidateImageContainers() {
    queryClient.invalidateQueries({ queryKey: ['image', imageId] });
    if (shootId) {
      queryClient.invalidateQueries({ queryKey: ['shoot', shootId, 'images'] });
    }
    if (setId) {
      queryClient.invalidateQueries({ queryKey: ['set', setId, 'images'] });
    }
    // Bilden kan finnas i flera sets utan att vi läst den vyn — invalidera
    // hubben + alla set-listor så att counts/cover stämmer överallt.
    queryClient.invalidateQueries({ queryKey: ['hub'] });
    queryClient.invalidateQueries({ queryKey: ['sets'] });
  }

  const ratingMutation = useMutation({
    mutationFn: ({ stars }: { stars: number }) => setRating(imageId!, stars),
    onMutate: async ({ stars }) => {
      if (!user || !image) return;
      const userName = user.name as UserName;
      await queryClient.cancelQueries({ queryKey: ['image', imageId] });
      const prev = queryClient.getQueryData<ImageDetail>(['image', imageId]);
      if (prev) {
        queryClient.setQueryData<ImageDetail>(['image', imageId], {
          ...prev,
          ratings: { ...prev.ratings, [userName]: stars },
        });
      }
      // Optimistic update även för parent-listor (set/shoot) så att
      // stjärnorna i grid-vyn slår igenom direkt när man backar.
      if (setId) {
        const setListKey = ['set', setId, 'images'] as const;
        const prevSetList = queryClient.getQueryData<Array<{ id: string; ratings?: Record<string, number> }>>(setListKey);
        if (prevSetList) {
          queryClient.setQueryData(
            setListKey,
            prevSetList.map((img) =>
              img.id === imageId
                ? { ...img, ratings: { ...img.ratings, [userName]: stars } }
                : img,
            ),
          );
        }
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['image', imageId], ctx.prev);
    },
    onSettled: invalidateImageContainers,
  });

  const favoriteMutation = useMutation({
    mutationFn: ({ favorite }: { favorite: boolean }) => setFavorite(imageId!, favorite),
    onMutate: async ({ favorite }) => {
      if (!user || !image) return;
      const userName = user.name as UserName;
      await queryClient.cancelQueries({ queryKey: ['image', imageId] });
      const prev = queryClient.getQueryData<ImageDetail>(['image', imageId]);
      if (prev) {
        queryClient.setQueryData<ImageDetail>(['image', imageId], {
          ...prev,
          favorites: { ...prev.favorites, [userName]: favorite },
        });
      }
      // Samma optimistic patch för set-list (se ratingMutation).
      if (setId) {
        const setListKey = ['set', setId, 'images'] as const;
        const prevSetList = queryClient.getQueryData<Array<{ id: string; favorites?: Record<string, boolean> }>>(setListKey);
        if (prevSetList) {
          queryClient.setQueryData(
            setListKey,
            prevSetList.map((img) =>
              img.id === imageId
                ? { ...img, favorites: { ...img.favorites, [userName]: favorite } }
                : img,
            ),
          );
        }
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['image', imageId], ctx.prev);
    },
    onSettled: invalidateImageContainers,
  });

  // Snabb-remove från set via ✕ på set-pillarna. Inkluderar optimistic
  // update så pillen försvinner direkt; invalidate på onSettled får
  // back-end-state att slå genom överallt.
  const removeFromSetMutation = useMutation({
    mutationFn: (setIdToRemove: string) => removeImageFromSet(setIdToRemove, imageId!),
    onMutate: async (setIdToRemove) => {
      await queryClient.cancelQueries({ queryKey: ['image', imageId, 'sets'] });
      const prev = queryClient.getQueryData<ImageSetMembership[]>(['image', imageId, 'sets']);
      if (prev) {
        queryClient.setQueryData<ImageSetMembership[]>(
          ['image', imageId, 'sets'],
          prev.filter((s) => s.id !== setIdToRemove),
        );
      }
      return { prev };
    },
    onError: (_e, _setIdToRemove, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['image', imageId, 'sets'], ctx.prev);
    },
    onSettled: (_data, _err, setIdToRemove) => {
      queryClient.invalidateQueries({ queryKey: ['image', imageId, 'sets'] });
      queryClient.invalidateQueries({ queryKey: ['set', setIdToRemove] });
      queryClient.invalidateQueries({ queryKey: ['set', setIdToRemove, 'images'] });
      queryClient.invalidateQueries({ queryKey: ['sets'] });
      queryClient.invalidateQueries({ queryKey: ['hub'] });
    },
  });

  const commentMutation = useMutation({
    mutationFn: (text: string) => postComment(imageId!, { text }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['image', imageId, 'comments'] });
      setDraftMessage('');
    },
  });

  // Base-path för prev/next och back-knapp. Beror på vilken context vi är i.
  const contextBasePath = inSetContext ? `/sets/${setId}` : `/shoots/${shootId}`;

  function navigateTo(targetId: string | undefined) {
    if (!targetId) return;
    navigate(`${contextBasePath}/images/${targetId}`);
  }

  useKeyboard(
    (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateTo(prevImage?.id);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateTo(nextImage?.id);
      } else if (e.key === 'Escape') {
        if (addSetOpen) {
          setAddSetOpen(false);
        } else if (isZoomed) {
          zoomRef.current?.zoomOut();
        } else if (mobilePanelOpen) {
          setMobilePanelOpen(false);
        } else {
          navigate(contextBasePath);
        }
      } else if (e.key === 'i' || e.key === 'I') {
        setPanelHidden((p) => !p);
      } else if (e.key === 'f' || e.key === 'F') {
        if (!user || !image) return;
        const cur = image.favorites?.[user.name as UserName] ?? false;
        favoriteMutation.mutate({ favorite: !cur });
      } else if (/^[1-5]$/.test(e.key)) {
        if (!user || !image) return;
        const stars = parseInt(e.key, 10);
        const cur = image.ratings?.[user.name as UserName] ?? 0;
        ratingMutation.mutate({ stars: cur === stars ? 0 : stars });
      } else if (e.key === 'm' || e.key === 'M') {
        setPanelHidden(false);
        setMobilePanelOpen(true);
        setTimeout(() => {
          const ta = document.getElementById('chat-input') as HTMLTextAreaElement | null;
          ta?.focus();
        }, 250);
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomRef.current?.zoomIn();
      } else if (e.key === '-' || e.key === '0') {
        e.preventDefault();
        zoomRef.current?.zoomOut();
      } else if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        setEditMode(true);
      }
    },
    { enabled: !editMode },
  );

  if (!imageQuery.isLoading && !image) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center gap-3 px-6">
        <div className="font-display text-3xl">Image not found</div>
        <Link
          to={contextBasePath}
          className="text-henrik underline underline-offset-4"
        >
          ← Back to gallery
        </Link>
      </div>
    );
  }

  return (
    <div
      className="h-screen h-[100dvh] overflow-hidden text-loupe-text"
      style={{ background: '#050505' }}
    >
      {!editMode ? (
        <Link
          to={contextBasePath}
          className="fixed top-3 sm:top-5 left-3 sm:left-6 z-[100] flex items-center gap-2.5 text-loupe-text-2 hover:text-loupe-text uppercase text-[11px] sm:text-xs tracking-[0.1em] py-2.5 px-3.5 bg-loupe-bg-2/70 backdrop-blur-xl border border-loupe-border-2 rounded-full min-h-[44px] transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {inSetContext ? setQuery.data?.name ?? 'Set' : 'Gallery'}
        </Link>
      ) : null}
      {!editMode && currentIdx >= 0 ? (
        <div className="fixed top-3 sm:top-5 right-3 sm:right-6 z-[100] text-[11px] sm:text-xs text-loupe-text-2 tracking-[0.1em] tabular-nums px-3.5 py-2.5 bg-loupe-bg-2/70 backdrop-blur-xl border border-loupe-border-2 rounded-full min-h-[44px] flex items-center">
          <strong className="text-loupe-text font-semibold">{currentIdx + 1}</strong>
          <span className="mx-1 text-loupe-text-3">/</span>
          <span>{images.length}</span>
        </div>
      ) : null}

      {editMode ? (
        <div className="fixed top-3 sm:top-5 left-1/2 -translate-x-1/2 z-[110] flex items-center gap-2 px-3.5 py-2 bg-henrik/15 backdrop-blur-xl border border-henrik/40 text-henrik text-[11px] tracking-[0.15em] uppercase rounded-full">
          <Brush className="w-3.5 h-3.5" />
          Editing — original is preserved
        </div>
      ) : null}

      {!editMode ? (
        <button
          onClick={() => setPanelHidden((p) => !p)}
          title="Hide info panel (i)"
          className={[
            'hidden sm:flex fixed top-1/2 -translate-y-1/2 z-[110] items-center justify-center',
            'w-8 h-16 rounded-l-xl bg-loupe-bg-2/90 backdrop-blur-xl border border-loupe-border-2 border-r-0',
            'text-loupe-text-2 hover:text-loupe-text transition-all duration-300',
          ].join(' ')}
          style={{
            right: panelHidden ? 0 : 'var(--panel-w)',
            transform: 'translate(50%, -50%)',
          }}
        >
          <ChevronRight
            className={`w-4 h-4 transition-transform duration-300 ${panelHidden ? 'rotate-180' : ''}`}
          />
        </button>
      ) : null}

      <div className="flex flex-col sm:flex-row h-screen h-[100dvh]">
        <div
          className={[
            'flex-1 relative flex items-center justify-center min-w-0 transition-all duration-300',
            editMode ? 'pt-0 pb-0 px-0' : 'pt-16 sm:pt-20 pb-20 sm:pb-10 px-3 sm:px-10',
          ].join(' ')}
          style={{
            background: 'radial-gradient(ellipse at center, #0f0f0f 0%, #050505 100%)',
          }}
        >
          {!editMode && prevImage && !isZoomed ? (
            <button
              onClick={() => navigateTo(prevImage.id)}
              aria-label="Previous image"
              className="absolute top-1/2 -translate-y-1/2 left-2 sm:left-6 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-loupe-bg-2/80 backdrop-blur-xl border border-loupe-border-2 text-loupe-text flex items-center justify-center hover:scale-105 transition-transform z-20"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          ) : null}
          {!editMode && nextImage && !isZoomed ? (
            <button
              onClick={() => navigateTo(nextImage.id)}
              aria-label="Next image"
              className="absolute top-1/2 -translate-y-1/2 right-2 sm:right-6 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-loupe-bg-2/80 backdrop-blur-xl border border-loupe-border-2 text-loupe-text flex items-center justify-center hover:scale-105 transition-transform z-20"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : null}

          {image ? (
            editMode ? (
              <HealingTool
                imageId={image.id}
                src={editedImageUrl(image.id, 'full')}
                naturalWidth={image.width}
                naturalHeight={image.height}
                onClose={() => setEditMode(false)}
                onSaved={() => {
                  queryClient.invalidateQueries({
                    queryKey: ['image', imageId, 'edits'],
                  });
                  setShowOriginalView(false);
                }}
              />
            ) : (
              <ZoomableImage
                ref={zoomRef}
                key={stageImageUrl}
                src={stagePreviewUrl}
                fullSrc={stageFullUrl}
                alt={image.filename}
                naturalWidth={image.width}
                naturalHeight={image.height}
                onZoomChange={setIsZoomed}
              />
            )
          ) : (
            <div className="text-loupe-text-3 text-sm">Loading…</div>
          )}

          {!editMode && hasEdits && image ? (
            <button
              onClick={() => setShowOriginalView((s) => !s)}
              title={
                showOriginalView
                  ? 'Showing original — click to see edited'
                  : 'Showing edited — click to see original'
              }
              className={[
                'absolute top-3 sm:top-5 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-[calc(var(--panel-w)+24px)]',
                'z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full',
                'text-[11px] tracking-[0.1em] uppercase backdrop-blur-xl border transition-colors',
                showOriginalView
                  ? 'bg-loupe-bg-2/80 border-loupe-border-2 text-loupe-text-2'
                  : 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
              ].join(' ')}
            >
              <Layers className="w-3 h-3" />
              {showOriginalView ? 'Original' : 'Edited'}
            </button>
          ) : null}
        </div>

        {!editMode ? (
          <SidePanel
            image={image}
            comments={comments}
            edits={edits}
            panelHidden={panelHidden}
            mobileOpen={mobilePanelOpen}
            onMobileClose={() => setMobilePanelOpen(false)}
            onRate={(stars) => ratingMutation.mutate({ stars })}
            onFavorite={(fav) => favoriteMutation.mutate({ favorite: fav })}
            draftMessage={draftMessage}
            onDraftChange={setDraftMessage}
            onSendMessage={() => {
              const text = draftMessage.trim();
              if (!text) return;
              commentMutation.mutate(text);
            }}
            isSending={commentMutation.isPending}
            viewer={user?.name as UserName | undefined}
            onEnterEditMode={() => setEditMode(true)}
            onOpenAddToSet={() => setAddSetOpen(true)}
            addSetBtnRef={addSetBtnRef}
            workspaceLoaded={!!workspaceId}
            imageSets={imageSets}
            onRemoveFromSet={(setIdToRemove) => removeFromSetMutation.mutate(setIdToRemove)}
            removingSetId={
              removeFromSetMutation.isPending
                ? (removeFromSetMutation.variables as string | undefined) ?? null
                : null
            }
          />
        ) : null}
      </div>

      {!editMode && !mobilePanelOpen && image && user ? (
        <div
          className="sm:hidden fixed bottom-[100px] left-1/2 -translate-x-1/2 z-[90] flex items-center gap-2 px-2.5 py-2 bg-loupe-bg-2/95 backdrop-blur-2xl border border-loupe-border-2 rounded-full shadow-[0_12px_40px_-8px_rgba(0,0,0,0.7)]"
        >
          <div className="px-2 text-gold tracking-tighter text-sm">
            {Array.from({ length: 5 }).map((_, i) => (
              <span
                key={i}
                className={
                  i < (image.ratings?.[user.name as UserName] ?? 0)
                    ? 'text-gold'
                    : 'text-white/20'
                }
              >
                ★
              </span>
            ))}
          </div>
          <button
            onClick={() => {
              const cur = image.favorites?.[user.name as UserName] ?? false;
              favoriteMutation.mutate({ favorite: !cur });
            }}
            className="w-11 h-11 flex items-center justify-center transition-colors"
            aria-label="Toggle favorite"
          >
            <Heart
              className={`w-5 h-5 ${
                image.favorites?.[user.name as UserName] ? 'fill-heart text-heart' : 'text-loupe-text-2'
              }`}
              strokeWidth={1.8}
            />
          </button>
          <button
            onClick={() => setMobilePanelOpen(true)}
            className="w-11 h-11 flex items-center justify-center bg-loupe-bg-3 rounded-full text-loupe-text"
            aria-label="Open info panel"
          >
            <Info className="w-5 h-5" />
          </button>
        </div>
      ) : null}

      {!editMode ? (
        <div
          className={[
            'hidden lg:flex fixed bottom-6 left-1/2 -translate-x-1/2 z-[100]',
            'gap-3.5 px-4.5 py-2.5 bg-loupe-bg-2/90 backdrop-blur-xl border border-loupe-border-2 rounded-full',
            'text-[11px] text-loupe-text-2 pointer-events-none transition-opacity',
            panelHidden ? 'opacity-55' : 'opacity-100',
          ].join(' ')}
        >
          <span><span className="kbd">←→</span> nav</span>
          <span><span className="kbd">1-5</span> rate</span>
          <span><span className="kbd">F</span> heart</span>
          <span><span className="kbd">M</span> message</span>
          <span><span className="kbd">+/-</span> zoom</span>
          <span><span className="kbd">E</span> edit</span>
          <span><span className="kbd">I</span> info</span>
          <span><span className="kbd">Esc</span> back</span>
        </div>
      ) : null}

      {/* Add to set popover (för enskild bild) */}
      {addSetOpen && workspaceId && imageId ? (
        <AddToSetPopover
          workspaceId={workspaceId}
          imageIds={[imageId]}
          onClose={() => {
            setAddSetOpen(false);
            // Invalidate sets-list för denna bild så chips uppdateras
            queryClient.invalidateQueries({ queryKey: ['image', imageId, 'sets'] });
          }}
          anchor="right"
          anchorRect={addSetBtnRef.current?.getBoundingClientRect() ?? null}
        />
      ) : null}
    </div>
  );
}

// ─── SidePanel ─────────────────────────────────────────────────────

interface SidePanelProps {
  image: ImageDetail | undefined;
  comments: Comment[];
  edits: ImageEdit[];
  panelHidden: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  onRate: (stars: number) => void;
  onFavorite: (favorite: boolean) => void;
  draftMessage: string;
  onDraftChange: (s: string) => void;
  onSendMessage: () => void;
  isSending: boolean;
  viewer: UserName | undefined;
  onEnterEditMode: () => void;
  onOpenAddToSet: () => void;
  addSetBtnRef: React.RefObject<HTMLButtonElement>;
  workspaceLoaded: boolean;
  imageSets: ImageSetMembership[];
  // Snabb-remove från ett set via ✕ på set-pillen.
  onRemoveFromSet: (setId: string) => void;
  // Set-ID som just nu håller på att tas bort (optimistic spinner-state).
  removingSetId: string | null;
}

function SidePanel({
  image,
  comments,
  edits,
  panelHidden,
  mobileOpen,
  onMobileClose,
  onRate,
  onFavorite,
  draftMessage,
  onDraftChange,
  onSendMessage,
  isSending,
  viewer,
  onEnterEditMode,
  onOpenAddToSet,
  addSetBtnRef,
  workspaceLoaded,
  imageSets,
  onRemoveFromSet,
  removingSetId,
}: SidePanelProps) {
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [comments.length]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 110)}px`;
  }, [draftMessage]);

  return (
    <aside
      className={[
        'flex flex-col bg-loupe-bg-2 border-loupe-border h-full',
        'sm:relative sm:border-l sm:w-[var(--panel-w)] sm:flex-shrink-0',
        'sm:transition-transform sm:duration-300',
        panelHidden ? 'sm:translate-x-full' : 'sm:translate-x-0',
        'fixed sm:static left-0 right-0 bottom-0 w-full',
        'h-[75vh] max-h-[75dvh] sm:h-full sm:max-h-none',
        'border-t border-loupe-border-2 sm:border-t-0',
        'rounded-t-2xl sm:rounded-none',
        'shadow-[0_-20px_60px_-10px_rgba(0,0,0,0.7)] sm:shadow-none z-[95]',
        'transition-transform duration-300',
      ].join(' ')}
      style={{
        transform: mobileOpen
          ? 'translateY(0)'
          : window.innerWidth < 800
            ? 'translateY(calc(100% - 80px))'
            : undefined,
      }}
    >
      <button
        type="button"
        onClick={() => (mobileOpen ? onMobileClose() : null)}
        className="sm:hidden absolute top-0 left-0 right-0 h-7 z-10 flex items-start justify-center pt-2"
        aria-label={mobileOpen ? 'Close info panel' : 'Open info panel'}
      >
        <div className="w-11 h-[5px] rounded-full bg-loupe-border-2" />
      </button>

      <div
        className="flex-1 overflow-y-auto scrollbar-thin px-5 sm:px-6 pt-7 sm:pt-20 pb-4 flex flex-col gap-6 min-h-0"
      >
        {image ? (
          <>
            {/* File block */}
            <div className="flex flex-col gap-2">
              <div className="text-[10px] tracking-[0.25em] uppercase text-loupe-text-3 font-semibold mb-1.5">
                File
              </div>
              <div className="font-mono text-sm text-loupe-text">{image.filename}</div>
              <div className="text-[11px] text-loupe-text-3 leading-relaxed">
                {image.width} × {image.height}
                {image.takenAt ? <> · {formatTimestamp(image.takenAt)}</> : null}
                {edits.length > 0 ? (
                  <>
                    {' · '}
                    <span className="text-emerald-400/80">
                      {edits.length} edit{edits.length !== 1 ? 's' : ''}
                    </span>
                  </>
                ) : null}
              </div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <DownloadButton imageId={image.id} filename={image.filename} />
                <button
                  type="button"
                  onClick={onEnterEditMode}
                  title="Enter healing tool (E)"
                  className={[
                    'flex items-center gap-1.5 px-3 py-1.5 text-[11px] min-h-[32px] rounded-md transition-colors border tracking-wide',
                    'bg-henrik/10 border-henrik/30 text-henrik hover:bg-henrik/20 hover:border-henrik/50',
                  ].join(' ')}
                >
                  <Brush className="w-3.5 h-3.5" />
                  Heal
                </button>
              </div>
            </div>

            {/* Ratings */}
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-loupe-text-3 font-semibold mb-3">
                Ratings
              </div>
              <div className="flex flex-col gap-2.5">
                <UserRateCard
                  user="henrik"
                  stars={image.ratings?.henrik ?? 0}
                  favorite={image.favorites?.henrik ?? false}
                  isViewer={viewer === 'henrik'}
                  onRate={onRate}
                  onFavorite={onFavorite}
                />
                <UserRateCard
                  user="michelle"
                  stars={image.ratings?.michelle ?? 0}
                  favorite={image.favorites?.michelle ?? false}
                  isViewer={viewer === 'michelle'}
                  onRate={onRate}
                  onFavorite={onFavorite}
                />
              </div>
            </div>

            {/* Sets */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.25em] uppercase text-loupe-text-3 font-semibold">
                  Sets
                </div>
                {imageSets.length > 0 ? (
                  <div className="text-[10px] text-loupe-text-3 tracking-[0.1em] tabular-nums">
                    {imageSets.length} {imageSets.length === 1 ? 'set' : 'sets'}
                  </div>
                ) : null}
              </div>
              {/* Chips för sets bilden tillhör. Klick på namnet öppnar setet;
                  ✕ till höger tar bort bilden från setet (snabb-remove utan
                  att behöva öppna popovern). */}
              {imageSets.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  {imageSets.map((s) => {
                    const isRemoving = removingSetId === s.id;
                    const borderColor = s.color
                      ? `${s.color}55`
                      : 'rgba(255,255,255,0.15)';
                    const bgColor = s.color
                      ? `${s.color}15`
                      : 'rgba(255,255,255,0.04)';
                    const textColor = s.color ?? 'var(--loupe-text-2)';
                    return (
                      <div
                        key={s.id}
                        className={[
                          'group inline-flex items-stretch rounded-full border overflow-hidden text-[11px] tracking-wide transition-opacity',
                          isRemoving ? 'opacity-50' : '',
                        ].join(' ')}
                        style={{ borderColor, background: bgColor, color: textColor }}
                      >
                        <Link
                          to={`/sets/${s.id}`}
                          title={`Open set: ${s.name}`}
                          className="inline-flex items-center gap-1.5 pl-2.5 pr-2 py-1 hover:bg-white/[0.04] transition-colors"
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ background: s.color ?? '#888' }}
                          />
                          {s.name}
                        </Link>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isRemoving) return;
                            onRemoveFromSet(s.id);
                          }}
                          disabled={isRemoving}
                          title={`Remove from ${s.name}`}
                          aria-label={`Remove from ${s.name}`}
                          className="inline-flex items-center justify-center w-6 pr-1.5 border-l border-current/20 opacity-50 hover:opacity-100 hover:bg-red-500/15 transition-all"
                          style={{ borderLeftColor: borderColor }}
                        >
                          <X className="w-3 h-3" strokeWidth={2.5} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <button
                ref={addSetBtnRef}
                type="button"
                onClick={onOpenAddToSet}
                disabled={!workspaceLoaded}
                title="Add this image to a set"
                className={[
                  'inline-flex items-center gap-1.5 px-3 py-2 text-[11px] min-h-[36px] rounded-md transition-colors border tracking-wide w-full justify-center',
                  workspaceLoaded
                    ? 'bg-henrik/10 border-henrik/30 text-henrik hover:bg-henrik/20 hover:border-henrik/50'
                    : 'bg-loupe-bg-3 border-loupe-border text-loupe-text-3 cursor-not-allowed',
                ].join(' ')}
              >
                <Plus className="w-3.5 h-3.5" />
                {imageSets.length > 0 ? 'Add to another set…' : 'Add to set…'}
              </button>
            </div>

            {/* Chat thread */}
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.25em] uppercase text-loupe-text-3 font-semibold">
                  Conversation
                </div>
                <div className="text-[10px] text-loupe-text-3 tracking-[0.1em] tabular-nums">
                  {comments.length} {comments.length === 1 ? 'message' : 'messages'}
                </div>
              </div>
              <div
                ref={threadRef}
                className="flex flex-col gap-3 px-0.5 py-2 overflow-y-auto scrollbar-thin flex-1 min-h-[120px]"
              >
                {comments.length === 0 ? (
                  <div className="text-loupe-text-3 text-sm py-8 text-center">
                    No messages yet. Say something about this frame.
                  </div>
                ) : (
                  comments.map((c) => <ChatBubble key={c.id} comment={c} />)
                )}
              </div>

              <div
                className={[
                  'mt-3 px-3 py-2.5 bg-loupe-bg-3 border border-loupe-border-2 rounded-2xl',
                  'flex items-end gap-2 transition-colors',
                  'focus-within:border-henrik/50',
                ].join(' ')}
              >
                <textarea
                  id="chat-input"
                  ref={inputRef}
                  value={draftMessage}
                  onChange={(e) => onDraftChange(e.target.value)}
                  placeholder="Write a message…"
                  rows={1}
                  className="flex-1 bg-transparent border-none text-loupe-text font-sans text-sm leading-snug resize-none outline-none px-1 py-1.5 min-h-[24px] max-h-[110px]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSendMessage();
                    }
                  }}
                />
                <button
                  onClick={onSendMessage}
                  disabled={isSending || draftMessage.trim().length === 0}
                  aria-label="Send message"
                  className="flex-shrink-0 min-w-[44px] min-h-[44px] w-11 h-11 -m-1 rounded-full bg-henrik border-none text-white flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              {viewer ? (
                <div className="text-[10px] text-loupe-text-3 tracking-[0.1em] uppercase mt-1.5 text-right px-1">
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                    style={{
                      background: viewer === 'henrik' ? 'var(--henrik)' : 'var(--michelle)',
                    }}
                  />
                  Posting as {viewer === 'henrik' ? 'Henrik' : 'Michelle'}
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <div className="text-loupe-text-3 text-sm">Loading…</div>
        )}
      </div>
    </aside>
  );
}

// ─── ChatBubble ────────────────────────────────────────────────────

function ChatBubble({ comment }: { comment: Comment }) {
  const author = authorOf(comment);
  if (!author) return null;
  const isMichelle = author === 'michelle';
  const time = comment.createdAt
    ? new Date(comment.createdAt).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : '';

  return (
    <div
      className={[
        'flex flex-col gap-1 max-w-[85%] animate-msgIn',
        isMichelle ? 'self-end items-end' : 'self-start items-start',
      ].join(' ')}
    >
      <div className="flex items-center gap-1.5 text-[10px] text-loupe-text-3 tracking-wide px-1">
        {!isMichelle ? (
          <>
            <span className="font-semibold text-[11px] text-henrik">Henrik</span>
            <span className="tabular-nums">{time}</span>
          </>
        ) : (
          <>
            <span className="tabular-nums">{time}</span>
            <span className="font-semibold text-[11px] text-michelle">Michelle</span>
          </>
        )}
      </div>
      <div
        className={[
          'px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-snug text-loupe-text border break-words',
          isMichelle
            ? 'bg-michelle/[0.12] border-michelle/[0.22] rounded-br-sm'
            : 'bg-henrik/[0.10] border-henrik/[0.20] rounded-bl-sm',
        ].join(' ')}
      >
        {comment.text}
      </div>
    </div>
  );
}

// ─── UserRateCard ──────────────────────────────────────────────────

interface UserRateCardProps {
  user: UserName;
  stars: number;
  favorite: boolean;
  isViewer: boolean;
  onRate: (stars: number) => void;
  onFavorite: (favorite: boolean) => void;
}

function UserRateCard({
  user,
  stars,
  favorite,
  isViewer,
  onRate,
  onFavorite,
}: UserRateCardProps) {
  const isHenrik = user === 'henrik';
  return (
    <div
      className={[
        'bg-loupe-bg-3 border border-loupe-border-2 rounded-2xl px-4 py-3.5 transition-colors',
        isHenrik ? 'hover:border-henrik/35' : 'hover:border-michelle/35',
      ].join(' ')}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5 text-sm font-medium">
          <span
            className={`w-2 h-2 rounded-full ${
              isHenrik ? 'bg-henrik' : 'bg-michelle'
            }`}
            style={{
              boxShadow: isHenrik
                ? '0 0 0 2px rgba(95,168,211,0.2)'
                : '0 0 0 2px rgba(230,57,70,0.2)',
            }}
          />
          {isHenrik ? 'Henrik' : 'Michelle'}
          {isViewer ? (
            <span className="text-[9px] uppercase tracking-[0.2em] text-loupe-text-3 ml-1">
              you
            </span>
          ) : null}
        </div>
        <HeartFavorite
          active={favorite}
          onClick={isViewer ? () => onFavorite(!favorite) : undefined}
          size="lg"
          ariaLabel={`${isHenrik ? 'Henrik' : 'Michelle'} favorite`}
        />
      </div>
      <StarRating
        value={stars}
        onChange={isViewer ? onRate : undefined}
        size="lg"
      />
    </div>
  );
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d
    .toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .replace(',', ' ·');
}
